import { type PropsWithChildren } from '@shared/utils';
import { createContext, useContext, useMemo } from 'react';

const DEFAULT_SETTINGS: PreviewSettings = {
  withTimer: true,
  shouldSortByExamSettings: true,
  shouldDisplayAnswer: false,
};

export interface PreviewSettings {
  withTimer: boolean;
  shouldSortByExamSettings: boolean;
  shouldDisplayAnswer: boolean;
}

interface ExamPreviewSettingsContextType {
  previewSettings: PreviewSettings;
}

const ExamPreviewSettingsContext = createContext<ExamPreviewSettingsContextType | undefined>(
  undefined,
);

function useExamPreviewSettings() {
  const context = useContext(ExamPreviewSettingsContext);

  if (!context)
    throw new Error('useExamPreviewSettings must be used within ExamPreviewSettingsProvider');

  return context;
}

function ExamPreviewSettingsProvider({
  children,
  initialSettings,
}: PropsWithChildren & {
  initialSettings?: PreviewSettings;
}) {
  const contextValue = useMemo(
    () => ({
      previewSettings: initialSettings ?? DEFAULT_SETTINGS,
    }),
    [initialSettings],
  );

  return (
    <ExamPreviewSettingsContext.Provider value={contextValue}>
      {children}
    </ExamPreviewSettingsContext.Provider>
  );
}

export { ExamPreviewSettingsProvider, useExamPreviewSettings };
